import { i18n } from "@tellescope/i18n";

export interface Address {
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  zipCode: string;
  zipPlusFour?: string;
  state: string;
}

/**
 * US state codes and labels for use in address forms.
 * Each entry contains a two-letter state code and full state name.
 */
export const US_STATES = [
  { value: "AK", getLabel: () => i18n.t("Alaska", { ns: "common" }) },
  { value: "AL", getLabel: () => i18n.t("Alabama", { ns: "common" }) },
  { value: "AR", getLabel: () => i18n.t("Arkansas", { ns: "common" }) },
  { value: "AS", getLabel: () => i18n.t("American Samoa", { ns: "common" }) },
  { value: "AZ", getLabel: () => i18n.t("Arizona", { ns: "common" }) },
  { value: "CA", getLabel: () => i18n.t("California", { ns: "common" }) },
  { value: "CO", getLabel: () => i18n.t("Colorado", { ns: "common" }) },
  { value: "CT", getLabel: () => i18n.t("Connecticut", { ns: "common" }) },
  { value: "DC", getLabel: () => i18n.t("Washington DC", { ns: "common" }) },
  { value: "DE", getLabel: () => i18n.t("Delaware", { ns: "common" }) },
  { value: "FL", getLabel: () => i18n.t("Florida", { ns: "common" }) },
  { value: "GA", getLabel: () => i18n.t("Georgia", { ns: "common" }) },
  { value: "GU", getLabel: () => i18n.t("Guam", { ns: "common" }) },
  { value: "HI", getLabel: () => i18n.t("Hawaii", { ns: "common" }) },
  { value: "IA", getLabel: () => i18n.t("Iowa", { ns: "common" }) },
  { value: "ID", getLabel: () => i18n.t("Idaho", { ns: "common" }) },
  { value: "IL", getLabel: () => i18n.t("Illinois", { ns: "common" }) },
  { value: "IN", getLabel: () => i18n.t("Indiana", { ns: "common" }) },
  { value: "KS", getLabel: () => i18n.t("Kansas", { ns: "common" }) },
  { value: "KY", getLabel: () => i18n.t("Kentucky", { ns: "common" }) },
  { value: "LA", getLabel: () => i18n.t("Louisiana", { ns: "common" }) },
  { value: "MA", getLabel: () => i18n.t("Massachusetts", { ns: "common" }) },
  { value: "MD", getLabel: () => i18n.t("Maryland", { ns: "common" }) },
  { value: "ME", getLabel: () => i18n.t("Maine", { ns: "common" }) },
  { value: "MI", getLabel: () => i18n.t("Michigan", { ns: "common" }) },
  { value: "MN", getLabel: () => i18n.t("Minnesota", { ns: "common" }) },
  { value: "MO", getLabel: () => i18n.t("Missouri", { ns: "common" }) },
  { value: "MP", getLabel: () => i18n.t("Northern Mariana Islands", { ns: "common" }) },
  { value: "MS", getLabel: () => i18n.t("Mississippi", { ns: "common" }) },
  { value: "MT", getLabel: () => i18n.t("Montana", { ns: "common" }) },
  { value: "NC", getLabel: () => i18n.t("North Carolina", { ns: "common" }) },
  { value: "ND", getLabel: () => i18n.t("North Dakota", { ns: "common" }) },
  { value: "NE", getLabel: () => i18n.t("Nebraska", { ns: "common" }) },
  { value: "NH", getLabel: () => i18n.t("New Hampshire", { ns: "common" }) },
  { value: "NJ", getLabel: () => i18n.t("New Jersey", { ns: "common" }) },
  { value: "NM", getLabel: () => i18n.t("New Mexico", { ns: "common" }) },
  { value: "NV", getLabel: () => i18n.t("Nevada", { ns: "common" }) },
  { value: "NY", getLabel: () => i18n.t("New York", { ns: "common" }) },
  { value: "OH", getLabel: () => i18n.t("Ohio", { ns: "common" }) },
  { value: "OK", getLabel: () => i18n.t("Oklahoma", { ns: "common" }) },
  { value: "OR", getLabel: () => i18n.t("Oregon", { ns: "common" }) },
  { value: "PA", getLabel: () => i18n.t("Pennsylvania", { ns: "common" }) },
  { value: "PR", getLabel: () => i18n.t("Puerto Rico", { ns: "common" }) },
  { value: "RI", getLabel: () => i18n.t("Rhode Island", { ns: "common" }) },
  { value: "SC", getLabel: () => i18n.t("South Carolina", { ns: "common" }) },
  { value: "SD", getLabel: () => i18n.t("South Dakota", { ns: "common" }) },
  { value: "TN", getLabel: () => i18n.t("Tennessee", { ns: "common" }) },
  { value: "TX", getLabel: () => i18n.t("Texas", { ns: "common" }) },
  { value: "UM", getLabel: () => i18n.t("United States Minor Outlying Islands", { ns: "common" }) },
  { value: "UT", getLabel: () => i18n.t("Utah", { ns: "common" }) },
  { value: "VA", getLabel: () => i18n.t("Virginia", { ns: "common" }) },
  { value: "VI", getLabel: () => i18n.t("Virgin Islands", { ns: "common" }) },
  { value: "VT", getLabel: () => i18n.t("Vermont", { ns: "common" }) },
  { value: "WA", getLabel: () => i18n.t("Washington", { ns: "common" }) },
  { value: "WI", getLabel: () => i18n.t("Wisconsin", { ns: "common" }) },
  { value: "WV", getLabel: () => i18n.t("West Virginia", { ns: "common" }) },
  { value: "WY", getLabel: () => i18n.t("Wyoming", { ns: "common" }) },
];

export type USStateCode = (typeof US_STATES)[number]["value"];

/**
 * Formats a US address into a standardized string representation.
 *
 * @template T - Type extending the Address interface
 * @param {T} address - The address object to format
 * @param {boolean} [multiLine=false] - Whether to format the address across multiple lines
 *
 * @returns {string} The formatted address string
 *
 * @remarks
 * - Single-line format: "123 Main St, Apt 4B, Springfield, IL 62701-1234"
 * - Multi-line format uses newline characters (\n) to separate lines
 * - ZIP+4 is appended if provided (format: ZIP-PLUS4)
 * - Address line two is optional and omitted if not provided
 *
 * @example
 * ```typescript
 * const address = {
 *   addressLineOne: "123 Main St",
 *   addressLineTwo: "Apt 4B",
 *   city: "Springfield",
 *   state: "IL",
 *   zipCode: "62701",
 *   zipPlusFour: "1234"
 * };
 *
 * // Single-line format
 * formatAddress(address);
 * // Returns: "123 Main St, Apt 4B, Springfield, IL 62701-1234"
 *
 * // Multi-line format
 * formatAddress(address, true);
 * // Returns:
 * // "123 Main St
 * // Apt 4B
 * // Springfield, IL 62701-1234"
 *
 * // Without address line two
 * formatAddress({ ...address, addressLineTwo: undefined });
 * // Returns: "123 Main St, Springfield, IL 62701-1234"
 *
 * // Without ZIP+4
 * formatAddress({ ...address, zipPlusFour: undefined });
 * // Returns: "123 Main St, Apt 4B, Springfield, IL 62701"
 * ```
 */
export const formatAddress = <T extends Address>(address: T, multiLine = false): string => {
  const { addressLineOne, addressLineTwo, city, state, zipCode, zipPlusFour } = address;

  // Format ZIP code with ZIP+4 if available
  const formattedZip = zipPlusFour ? `${zipCode}-${zipPlusFour}` : zipCode;

  // Format city, state, ZIP line
  const cityStateZip = city || state || formattedZip ? `${city}, ${state} ${formattedZip}` : "";

  if (multiLine) {
    // Multi-line format
    const lines = [addressLineOne];
    if (addressLineTwo) {
      lines.push(addressLineTwo);
    }
    lines.push(cityStateZip);
    return lines.filter(Boolean).join("\n");
  } else {
    // Single-line format
    const parts = [addressLineOne];
    if (addressLineTwo) {
      parts.push(addressLineTwo);
    }
    parts.push(cityStateZip);
    return parts.filter(Boolean).join(", ");
  }
};
